import { useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import BREAKPOINTS from 'styles/breakpoints';
import COLORS from 'styles/colors';
import { H6 } from 'styles/typography';

import ChevronSvg from 'public/images/icons/chevron.svg';

import RichText from './RichText';

const Item = styled.li`
  border-bottom: 1px solid ${COLORS.dawn.opacity(0.3)};
  color: ${({ textColor }) => COLORS[textColor]};
`;

const Label = styled.div`
  padding: 24px 20px 25px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  ${BREAKPOINTS.medium`
    padding: 37px 12px 36px 0;
  `}
`;

const Chevron = styled(ChevronSvg)`
  margin-left: 16px;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  transform: ${({ $isActive }) =>
    $isActive ? 'rotate(180deg)' : 'rotate(0deg)'};
  transform-origin: 8px 11px;
  transition: 0.4s;

  path {
    stroke-width: 5px;
    stroke: ${({ $strokeColor }) => COLORS[$strokeColor]};
  }

  ${BREAKPOINTS.medium`
    width: 25px;
    height: 25px;
    transform-origin: 12px 16px;

    path {
      stroke-width: 3px;
    }
  `}
`;

const Content = styled.div`
  max-height: var(--accordion-height);
  transition: max-height 0.3s ease-out;
  overflow: hidden;

  p {
    margin: 0 0 16px;

    &:last-child {
      margin: 0;
    }
  }

  ${({ isActive }) =>
    isActive &&
    css`
      transition-timing-function: ease-in;
    `}
`;

const ExpandedContent = styled.div`
  padding: 0 15px;
  transition: padding 0.3s ease-out;

  ${({ isActive }) =>
    isActive &&
    css`
      padding: 5px 15px 30px;
      transition-timing-function: ease-in;
    `}

  ${BREAKPOINTS.medium`
    padding-left: 0;
  `}
`;

const AccordionItem = ({ label, content, color, className }) => {
  const [active, setActive] = useState(false);
  const [height, setHeight] = useState(0);
  const ref = useRef(null);

  const toggleAccordion = () => {
    if (active) {
      setActive(false);
      setHeight(0);
    } else {
      setActive(true);
      setHeight(`${ref.current.scrollHeight + 30}px`);
    }
  };

  return (
    <Item textColor={color} className={className}>
      <Label
        onClick={toggleAccordion}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            toggleAccordion();
          }
        }}
        aria-expanded={active ? 'true' : 'false'}
        tabIndex={0}
      >
        <H6 as="h3">{label}</H6>
        <Chevron $isActive={active} $strokeColor={color} />
      </Label>
      <Content isActive={active} style={{ '--accordion-height': height }}>
        <ExpandedContent ref={ref} isActive={active}>
          <RichText html={content?.html} />
        </ExpandedContent>
      </Content>
    </Item>
  );
};

export default AccordionItem;
